<template>
  <div class="row desktop-header justify-content-center">
    <div class="col-12 col-md-10 col-lg-10 desktop-header-container">
      <div class="d-flex justify-content-between pb-2">
        <div class="desktop-header-title mr-auto d-flex text-extra-large">
          <back-button class="mr-2" @back="$emit('back')" v-if="showBackButton" />
          <span v-if="$slots.title"><slot name="title"></slot></span>
          <span class="bold" v-else-if="heading" :class="headerClass">{{ heading }}</span>
          <span class="bold" v-else-if="team" v-no-capture>{{ team.name }}</span>
          <span class="bold" v-else
            ><span class="shine" style="width: 3em; height: 1em;"></span
          ></span>
          <div class="team-settings ml-2" v-if="team && showSettings">
            <team-settings
              :team="team"
              v-if="team.parentId"
              @team-archived="$emit('team-archived')"
              @team-unarchived="$emit('team-unarchived')"
            >
              <action-icon
                icon="ellipsis-horizontal-icon"
                class="align-self-center action-icon-component flex-shrink-0"
              />
            </team-settings>
            <icon-dropdown
              icon-class="ellipsis-horizontal-icon"
              @change="onOrgSettingsSelection"
              :options="orgDropdownOptions"
              v-else-if="isOrgOwner"
            />
          </div>
        </div>
        <div class="d-flex align-items-center position-relative">
          <notification-center class="mr-2" :org-id="orgId" />
          <div v-if="showNewGoalButton" class="mr-2 d-flex" id="add-goal-link">
            <router-link
              :to="{
                name: 'new-goal',
                params: { orgId },
                query: {
                  teamId,
                  expanded: showNewGoalExpanded
                }
              }"
              class="new-goal-button btn-inline add-icon text-nowrap"
            >
              New goal
            </router-link>
          </div>
          <action-tooltip
            placement="left"
            target="add-goal-link"
            text="goal.new"
            :actions="[]"
            v-if="showNewGoalButton"
          />
          <div id="timeline-dropdown" class="timeline-dropdown">
            <timeline-dropdown
              class="mr-2"
              :intervals="intervals"
              :selected-interval="selectedInterval"
              :show-inactive-timeline-info="showInactiveTimelineInfo"
              :team-id="teamId"
              @change="$emit('update:selectedInterval', $event)"
            />
          </div>
          <i class="search-icon pointer" @click="openSearch" id="spotlight-search"></i>
          <action-tooltip target="spotlight-search" text="Search" :actions="['/']" />
        </div>
      </div>
      <team-members-list
        v-if="showTeamMembers"
        :org-id="orgId"
        :team-id="teamId"
        :sub-org-id="subOrgId"
        @manageTeamMembers="showManageTeamMembers = true"
        @manageOrgMembers="showInviteModal = true"
      />
      <manage-team-members
        v-if="teamId && showManageTeamMembers"
        v-model="showManageTeamMembers"
        :org-id="orgId"
        :team-id="teamId"
      />
      <org-invite-modal v-model="showInviteModal" v-if="showInviteModal" :org-id="orgId" />
    </div>
  </div>
</template>

<script>
import ActionIcon from '@/components/ActionIcon.vue'
import ActionTooltip from '@/components/ActionTooltip'
import BackButton from '@/components/BackButton'
import IconDropdown from '@/components/IconDropdown'
import ManageTeamMembers from '@/components/ManageTeamMembers'
import NotificationCenter from '@/components/notifications/NotificationCenter'
import OrgInviteModal from '@/components/OrgInviteModal'
import TeamSettings from '@/components/team/TeamSettings.vue'
import TeamMembersList from '@/components/TeamMembersList.vue'
import TimelineDropdown from '@/components/TimelineDropdown'
import { ADMIN_PANEL_TAB_ID } from '@/constants/adminPanel'
import events from '@/constants/events'
import EventBus from '@/EventBus'
import TeamMixin from '@/mixins/TeamMixin'
import isOwner from '@/utils/isOwner'
import { getAdminPageRouteConfig } from '@/utils/navigationConfigs'
import * as R from 'ramda'

/**
 * This component serves as page header. It is built to be shown in Desktop screens.
 *
 * This should be rendered only if `MediaQueryMixin.isDesktopMode` is true.
 */
export default {
  name: 'DesktopHeader',
  props: {
    orgId: [String, Number], // FIXME: this should only be a number
    teamId: [String, Number],
    selectedInterval: Object,
    intervals: Array,
    showNewGoalButton: {
      type: Boolean,
      default: true
    },
    showNewGoalExpanded: {
      type: Boolean,
      default: false
    },
    showTeamMembers: {
      type: Boolean,
      default: true
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    heading: String,
    showSettings: { default: false },
    headerClass: { default: '' },
    showInactiveTimelineInfo: { default: false }
  },
  components: {
    ActionIcon,
    TeamSettings,
    TimelineDropdown,
    IconDropdown,
    TeamMembersList,
    ManageTeamMembers,
    OrgInviteModal,
    ActionTooltip,
    NotificationCenter,
    BackButton
  },
  data() {
    return {
      showManageTeamMembers: false,
      showInviteModal: false
    }
  },
  mixins: [TeamMixin],
  computed: {
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    isOrgOwner() {
      return isOwner(this.org.memberships || [], this.$store.getters.user.id)
    },
    subOrgId() {
      return this.getClosestOrgLikeTeamId(this.teamId)
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    orgDropdownOptions() {
      return [{ label: 'Manage Org members', value: 'manageOrgMembers' }]
    }
  },
  methods: {
    openSearch() {
      EventBus.$emit(events.OPEN_SPOTLIGHT_SEARCH)
    },
    onOrgSettingsSelection({ value: selectionType }) {
      if (selectionType === 'manageOrgMembers') {
        const location = getAdminPageRouteConfig({
          orgId: this.orgId,
          subOrgId: this.subOrgId,
          query: {
            section: ADMIN_PANEL_TAB_ID.ORG_MEMBERS_TAB_ID
          }
        })
        this.$router.push(location)
      }
    },
    async fetchTeam() {
      if (this.teamId && !this.$store.getters.teamById(this.teamId)) {
        try {
          await this.$store.dispatch('getTeam', { orgId: this.orgId, teamId: this.teamId })
        } catch (e) {
          const status = R.path(['response', 'status'], e)
          if (status === 403) {
            EventBus.$emit('SHOW_403', { message: 'You do not have access to this team' })
          } else {
            EventBus.$emit('SHOW_404')
          }
        }
      }
    }
  },
  mounted() {
    this.fetchTeam()
  },
  watch: {
    teamId() {
      this.fetchTeam()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';

.desktop-header {
  padding: 15px 16px 03px 16px;
}

.desktop-header-title {
  line-height: 25px;
}

.desktop-header-title .shine {
  border-radius: 10px;
}

.new-goal-button {
  text-decoration: none;
  font-size: 13px;

  &:hover {
    color: initial;
  }

  &.btn-inline {
    border: none;
    padding-top: 3px;
    padding-bottom: 3px;
  }
}

.desktop-header-container {
  padding: 0 65px;
}

.timeline-dropdown {
  line-height: 1;
}

.btn-inline {
  &.add-icon:before {
    content: ' ';
    background: url(~@/assets/close.svg) no-repeat;
    width: 10px;
    height: 10px;
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
  }
}
</style>
